import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useUser } from 'app/user';
import {
  getAlertSetting,
  postAlertSetting,
  getEstateAddrList,
} from 'utils/api';

const MAX_REGIONS = { basic: 5, premium: 10 };

const AlertSettings = () => {
  const { plan, isFreeUser } = useUser();

  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error

  const [regions, setRegions] = useState([]);
  const [isActive, setIsActive] = useState(true);

  const [sidoList, setSidoList] = useState([]);
  const [gunguList, setGunguList] = useState([]);
  const [selectedSido, setSelectedSido] = useState('');
  const [selectedGungu, setSelectedGungu] = useState('');

  const isInitialized = useRef(false);
  const autoSaveTimer = useRef(null);

  const maxRegions = MAX_REGIONS[plan] || 0;

  useEffect(() => {
    if (!isFreeUser) {
      fetchSettings();
      fetchSidoList();
    }
  }, [isFreeUser]);

  // 변경사항 자동 저장
  useEffect(() => {
    if (!isInitialized.current) return;
    if (regions.length === 0) {
      setSaveStatus('idle');
      return;
    }

    setSaveStatus('saving');
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        await postAlertSetting({ regions, isActive });
        setSaveStatus('saved');
      } catch {
        setSaveStatus('error');
      }
    }, 600);

    return () => clearTimeout(autoSaveTimer.current);
  }, [regions, isActive]);

  const fetchSettings = async () => {
    try {
      const res = await getAlertSetting();
      if (res.statusCode === 200 && res.results) {
        setRegions(res.results.regions || []);
        setIsActive(res.results.isActive !== false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTimeout(() => {
        isInitialized.current = true;
      }, 100);
    }
  };

  const fetchSidoList = async () => {
    try {
      const res = await getEstateAddrList({ source: 'all' });
      if (res.statusCode === 200 && res.results) {
        setSidoList(res.results.items || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGunguList = async (sido) => {
    try {
      const res = await getEstateAddrList({ sido, source: 'all' });
      if (res.statusCode === 200 && res.results) {
        setGunguList(res.results.items || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSidoChange = (e) => {
    const sido = e.target.value;
    setSelectedSido(sido);
    setSelectedGungu('');
    setGunguList([]);
    if (sido) fetchGunguList(sido);
  };

  const handleAddRegion = () => {
    if (!selectedSido || !selectedGungu) return;
    const region = `${selectedSido}_${selectedGungu}`;
    if (regions.includes(region)) {
      alert('이미 추가된 지역입니다.');
      return;
    }
    if (regions.length >= maxRegions) {
      alert(
        `${plan.toUpperCase()} 플랜은 최대 ${maxRegions}개 지역까지 설정 가능합니다.`,
      );
      return;
    }
    setRegions([...regions, region]);
    setSelectedSido('');
    setSelectedGungu('');
    setGunguList([]);
  };

  const handleRemoveRegion = (region) => {
    setRegions(regions.filter((r) => r !== region));
  };

  if (isFreeUser) {
    return (
      <Container>
        <Card>
          <Title>급매 알림 설정</Title>
          <EmptyState>
            <p>급매 알림은 유료 플랜(Basic, Premium)에서만 이용 가능합니다.</p>
          </EmptyState>
        </Card>
      </Container>
    );
  }

  const sendFrequency =
    plan === 'premium' ? '주 3회 (월/수/금)' : '주 1회 (월~금 중)';

  return (
    <Container>
      <Card>
        <TitleRow>
          <Title>급매 알림 설정</Title>
          <SaveIndicator status={saveStatus}>
            {saveStatus === 'saving' && '저장 중...'}
            {saveStatus === 'saved' && '✓ 저장됨'}
            {saveStatus === 'error' && '저장 실패'}
          </SaveIndicator>
        </TitleRow>

        {/* 알림 ON/OFF */}
        <AlarmToggleRow>
          <AlarmToggleLabel>알림 받기</AlarmToggleLabel>
          <ToggleSwitch
            active={isActive}
            onClick={() => setIsActive((prev) => !prev)}
          >
            <ToggleKnob active={isActive} />
          </ToggleSwitch>
          <AlarmToggleStatus active={isActive}>
            {isActive ? 'ON' : 'OFF'}
          </AlarmToggleStatus>
        </AlarmToggleRow>

        {/* 발송 정보 */}
        <InfoGrid>
          <InfoItem>
            <InfoLabel>발송 주기</InfoLabel>
            <InfoValue>{sendFrequency}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>발송 시간</InfoLabel>
            <InfoValue>오전 9시</InfoValue>
          </InfoItem>
        </InfoGrid>

        {/* 지역 선택 */}
        <SectionTitle>
          알림 지역 선택 ({regions.length}/{maxRegions})
        </SectionTitle>
        <SelectRow>
          <StyledSelect value={selectedSido} onChange={handleSidoChange}>
            <option value="">시/도 선택</option>
            {sidoList.map((sido) => (
              <option key={sido} value={sido}>
                {sido}
              </option>
            ))}
          </StyledSelect>
          <StyledSelect
            value={selectedGungu}
            onChange={(e) => setSelectedGungu(e.target.value)}
            disabled={!selectedSido}
          >
            <option value="">구/군 선택</option>
            {gunguList.map((gungu) => (
              <option key={gungu} value={gungu}>
                {gungu}
              </option>
            ))}
          </StyledSelect>
          <AddButton
            onClick={handleAddRegion}
            disabled={
              !selectedSido || !selectedGungu || regions.length >= maxRegions
            }
          >
            추가
          </AddButton>
        </SelectRow>

        <RegionTable>
          <thead>
            <tr>
              <Th>지역</Th>
              <Th style={{ width: '60px', textAlign: 'center' }}>삭제</Th>
            </tr>
          </thead>
          <tbody>
            {regions.length === 0 ? (
              <tr>
                <EmptyTd colSpan={2}>알림 받을 지역을 추가해주세요</EmptyTd>
              </tr>
            ) : (
              regions.map((region) => (
                <tr key={region}>
                  <Td>{region.replace('_', ' ')}</Td>
                  <Td style={{ textAlign: 'center' }}>
                    <RemoveButton onClick={() => handleRemoveRegion(region)}>
                      ×
                    </RemoveButton>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </RegionTable>
        <HintText>
          {plan === 'basic' ? 'BASIC: 최대 5개 구' : 'PREMIUM: 최대 10개 구'}{' '}
          선택 가능
        </HintText>

        {/* 알림 기준 안내 */}
        <SectionTitle>알림 기준</SectionTitle>
        <HintText>
          KB시세 미만 매물 또는 동일 평형 평균 대비 10% 이상 저렴한 매물이
          알림에 포함됩니다. (저층 1~3층 제외)
        </HintText>
      </Card>
    </Container>
  );
};

const Container = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 40px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #232d42;
  margin: 0;
`;

const SaveIndicator = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ status }) =>
    status === 'saved'
      ? '#38a169'
      : status === 'error'
      ? '#e53e3e'
      : '#a3aed0'};
`;

const AlarmToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1.5px solid #f0f0f0;
  margin-bottom: 8px;
`;

const AlarmToggleLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #232d42;
  flex: 1;
`;

const ToggleSwitch = styled.div`
  width: 48px;
  height: 26px;
  border-radius: 13px;
  background-color: ${({ active }) => (active ? '#644ded' : '#d1d5db')};
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s;
  flex-shrink: 0;
`;

const ToggleKnob = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  position: absolute;
  top: 3px;
  left: ${({ active }) => (active ? '25px' : '3px')};
  transition: left 0.2s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
`;

const AlarmToggleStatus = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ active }) => (active ? '#644ded' : '#a3aed0')};
  width: 28px;
`;

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #232d42;
  margin: 24px 0 12px;
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #8f9bba;
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #232d42;
`;

const SelectRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
`;

const StyledSelect = styled.select`
  flex: 1;
  padding: 10px 12px;
  border: 1.5px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  color: #232d42;
  background: #fff;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #644ded;
  }

  &:disabled {
    background: #f7f7f7;
    cursor: not-allowed;
  }
`;

const AddButton = styled.button`
  padding: 10px 18px;
  background-color: #644ded;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #5338c9;
  }

  &:disabled {
    background-color: #a3aed0;
    cursor: not-allowed;
  }
`;

const RegionTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 4px;
`;

const Th = styled.th`
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #8f9bba;
  text-align: left;
  border-bottom: 1.5px solid #f0f0f0;
`;

const Td = styled.td`
  padding: 12px 12px;
  font-size: 14px;
  color: #232d42;
  border-bottom: 1px solid #f7f7f7;
`;

const EmptyTd = styled.td`
  padding: 20px 12px;
  font-size: 13px;
  color: #a3aed0;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #a3aed0;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0;

  &:hover {
    color: #e53e3e;
  }
`;

const HintText = styled.p`
  font-size: 12px;
  color: #a3aed0;
  margin: 4px 0 0;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #8f9bba;
`;

export default AlertSettings;
